import { AnimatePresence, motion } from "motion/react";
import React, {
  ComponentProps,
  PropsWithChildren,
  SyntheticEvent,
} from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ExternalLinkIcon } from "../../ui-library/_icons/ExternalLinkIcon.tsx";
import styles from "./Citation.module.css";
import { CitationIcon } from "./CitationIcon.tsx";
import { CitationComponent } from "./MarkdownWithCitations.tsx";
import { clx } from "../../utilities/clx.ts";
import { ProductEvent } from "../../adapters/product-events/ProductEventTarget.ts";
import { ProductEventContext } from "../../adapters/product-events/ProductEventContext.ts";

export const Citation: React.FC<
  ComponentProps<CitationComponent> & {
    numbered?: boolean;
    hideIds?: boolean;
    loading?: boolean;
    onDownload?: (href: string) => void;
    /**
     * Indicates if the referenced source file is currently being downloaded
     */
    downloading?: boolean;
  }
> = (props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const iconTitle = props.loading
    ? "Loading citation..."
    : "Click to show citation";
  React.useEffect(() => {
    if (props.loading) {
      setIsOpen(false);
    }
  }, [props.loading]);
  const productEventTarget = React.useContext(ProductEventContext);

  // Don't render anything if there's no citation data
  if (!props.data || props.data.length === 0) {
    return null;
  }

  return (
    <>
      <span
        onClick={() => {
          if (props.loading) return;
          setIsOpen(!isOpen);
          productEventTarget?.dispatchEvent(
            new ProductEvent("citation_expand"),
          );
        }}
        className={clx([styles.CitationIcon, props.loading && styles.Loading])}
        role={"button"}
        aria-label={iconTitle}
        title={iconTitle}
      >
        {props.numbered ? (
          <CitationNumbers referenceIds={props.referenceIds} />
        ) : (
          <CitationIcon />
        )}
      </span>
      <AnimatePresence>
        {isOpen && (
          <CitationWrapper>
            <a
              onClick={(e) => {
                e.preventDefault();
                setIsOpen(false);
              }}
              className={styles.Hide}
            >
              Click to hide
            </a>
            <div className={styles.CitationBox}>
              {props.data.map(
                ({ excerpt, id, page, sourceUrl, title }, index) => (
                  <div key={id} className={styles.CitationItem}>
                    {index > 0 && <hr className={styles.CitationDivider} />}
                    <CitationTitle
                      href={sourceUrl}
                      id={id}
                      title={
                        title + (props.downloading ? " (downloading...)" : "")
                      }
                      page={page}
                      hideIds={props.hideIds}
                      onClick={(e) => {
                        if (sourceUrl) {
                          productEventTarget?.dispatchEvent(
                            new ProductEvent("citation_follow"),
                          );
                          if (props.onDownload) {
                            e.preventDefault();
                            props.onDownload?.(sourceUrl);
                          }
                        }
                      }}
                    />
                    <Markdown remarkPlugins={[remarkGfm]}>{excerpt}</Markdown>
                  </div>
                ),
              )}
            </div>
          </CitationWrapper>
        )}
      </AnimatePresence>
    </>
  );
};

const CitationWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <motion.div
      layout
      initial={{
        opacity: 0,
        y: -20,
        height: 0,
        margin: "14px 24px",
      }}
      animate={{ opacity: 1, y: 0, height: "auto" }}
      exit={{ opacity: 0, y: -20, height: 0 }}
    >
      {children}
    </motion.div>
  );
};

const CitationTitle: React.FC<{
  href?: string;
  hideIds?: boolean;
  id: string;
  title: string;
  page: string;
  onClick?: (e: SyntheticEvent) => void;
}> = (props) => (
  <h1 className={styles.CitationTitle}>
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={props.onClick}
    >
      {[
        !props.hideIds && props.id + ". ",
        props.title,
        props.page && ` - Page: ${props.page}`,
      ]
        .filter(Boolean)
        .join(" ")}{" "}
      {props.href && <ExternalLinkIcon />}
    </a>
  </h1>
);

const CitationNumbers = (props: { referenceIds: string[] }) => {
  return (
    <span className={styles.CitationNumbers}>
      {props.referenceIds.join(", ")}
    </span>
  );
};
